import { useState, useEffect } from 'react';
import { clientService, Client, CreateClientData } from '../../services/client.service';
import { CashPaymentModal } from '../../components/payments/CashPaymentModal';

const INITIAL_FORM: CreateClientData = {
  email: '',
  name: '',
  phone: '',
  address: '',
  company: '',
  tax_id: '',
};

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCashPayment, setShowCashPayment] = useState(false);
  const [formData, setFormData] = useState<CreateClientData>(INITIAL_FORM);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await clientService.getClients();
      setClients(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar clientes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingClient) {
        await clientService.updateClient(editingClient.id, formData);
      } else {
        await clientService.createClient(formData);
      }
      await loadClients();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar cliente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este cliente?')) return;
    try {
      await clientService.deleteClient(id);
      await loadClients();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar cliente');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await clientService.toggleClientStatus(id);
      await loadClients();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      name: client.name,
      phone: client.phone || '',
      address: client.address || '',
      company: client.company || '',
      tax_id: client.tax_id || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setFormData(INITIAL_FORM);
    setError('');
  };

  const filteredClients = clients.filter(client => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      client.name.toLowerCase().includes(term) ||
      client.email.toLowerCase().includes(term) ||
      (client.company && client.company.toLowerCase().includes(term));
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const activeFiltersCount = filterStatus !== 'all' ? 1 : 0;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Clientes</h1>
          <p className="subtitle">Gestión de clientes del despacho</p>
        </div>
        <div className="actions">
          <button onClick={loadClients} className="btn btn-ghost">
            🔄 Actualizar
          </button>
          <button
            onClick={() => {
              setEditingClient(null);
              setFormData(INITIAL_FORM);
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            + Nuevo Cliente
          </button>
          <button
            onClick={() => setShowCashPayment(true)}
            className="btn btn-secondary"
            >
            💵 Registrar pago en efectivo
          </button>
        </div>
      </div>

      {/* Error global */}
      {error && !showModal && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-search">
          <span className="toolbar-search-icon">🔍</span>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por nombre, email o empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          {activeFiltersCount > 0 && (
            <span className="filter-badge">
              {activeFiltersCount} filtro activo
            </span>
          )}
        </div>
      </div>

      {/* Contenido */}
      {isLoading && clients.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando clientes...</p>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {clients.length === 0 ? '👥' : '🔍'}
          </div>
          <h3 className="empty-state-title">
            {clients.length === 0 ? 'No hay clientes registrados' : 'Sin resultados'}
          </h3>
          <p className="empty-state-text">
            {clients.length === 0
              ? 'Comienza registrando el primer cliente del despacho.'
              : 'No se encontraron clientes con los filtros aplicados.'}
          </p>
          {clients.length === 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary"
            >
              + Crear primer cliente
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Empresa</th>
                <th>RFC</th>
                <th className="col-center">Estado</th>
                <th className="col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="user-avatar" style={{ width: '36px', height: '36px', fontSize: '16px' }}>
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{client.name}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          ID: {client.id.slice(0, 8)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{client.email}</td>
                  <td>{client.phone || '—'}</td>
                  <td>{client.company || '—'}</td>
                  <td>{client.tax_id || '—'}</td>
                  <td className="col-center">
                    <span className={`badge ${client.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                      {client.status === 'active' ? '✅ Activo' : '⛔ Inactivo'}
                    </span>
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons-group">
                      <button
                        onClick={() => handleEdit(client)}
                        className="btn-action-edit"
                        title="Editar cliente"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleToggleStatus(client.id)}
                        className="btn-action-toggle"
                        title={client.status === 'active' ? 'Desactivar' : 'Activar'}
                      >
                        {client.status === 'active' ? '⛔' : '✅'}
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="btn-action-delete"
                        title="Eliminar cliente"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingClient ? '✏️ Editar Cliente' : '➕ Nuevo Cliente'}</h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Nombre completo</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre del cliente"
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label required">Correo electrónico</label>
                    <input
                      type="email"
                      className="form-input"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="cliente@correo.com"
                      disabled={!!editingClient}
                    />
                    {editingClient && (
                      <span className="form-help">El email no se puede modificar</span>
                    )}
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label">Teléfono</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="81 1234 5678"
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label">Empresa</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.company || ''}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Nombre de la empresa"
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label">RFC</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.tax_id || ''}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value.toUpperCase() })}
                      placeholder="XAXX010101000"
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label">Dirección</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Calle, número, colonia, ciudad"
                    />
                  </div>
                </div>

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Guardando...' : editingClient ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showCashPayment && (
        <CashPaymentModal
            onClose={() => setShowCashPayment(false)}
            onSuccess={() => {
            setShowCashPayment(false);
            loadClients();
            }}
        />
       )}
    </div>
  );
}